import { useEffect, useState } from "react";
import { FileBarChart, ChevronRight } from "lucide-react";
import { ProfitAndLoss } from "@/interfaces/profitAndLoss.interface";
import { fetchProfitAndLoss } from "@/services/profitLoss.service";
import { formatAmount } from "@/utils/expenseFormatters";
import { DateFilterKey, resolveDateFilter } from "@/utils/dateFilters";

interface ProfitAndLossCardProps {
  dateFilterKey: DateFilterKey | "";
  dateCustomFrom?: Date;
  dateCustomTo?: Date;
  onOpen: () => void;
}

const ProfitAndLossCard = ({ dateFilterKey, dateCustomFrom, dateCustomTo, onOpen }: ProfitAndLossCardProps) => {
  const [pl, setPl] = useState<ProfitAndLoss | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const range = resolveDateFilter(dateFilterKey || "this-year", dateCustomFrom, dateCustomTo) ?? {
      startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10),
      endDate: new Date().toISOString().slice(0, 10),
    };
    let cancelled = false;
    setLoading(true);
    fetchProfitAndLoss(range)
      .then((data) => {
        if (!cancelled) setPl(data);
      })
      .catch(() => {
        if (!cancelled) setPl(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dateFilterKey, dateCustomFrom, dateCustomTo]);

  const findTotal = (key: string) => pl?.sections.find((s) => s.key === key)?.total ?? 0;
  const income = findTotal("income");
  const expenses = findTotal("expenses");
  const netIncome = findTotal("netIncome");

  return (
    <button
      onClick={onOpen}
      className="w-full text-left rounded-xl border bg-card p-5 shadow-sm hover:shadow-md hover:border-primary/40 transition-all group"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <FileBarChart className="h-4.5 w-4.5 text-primary" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">Profit & Loss</h3>
            <p className="text-xs text-muted-foreground">Consolidated across all entities</p>
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-primary transition-colors shrink-0 mt-1" />
      </div>

      {loading ? (
        <div className="h-16 flex items-center">
          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Income</p>
            <p className="text-sm font-semibold tabular-nums text-emerald-600">{formatAmount(income)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Expenses</p>
            <p className="text-sm font-semibold tabular-nums text-rose-600">{formatAmount(expenses)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Net Income</p>
            <p className={`text-sm font-semibold tabular-nums ${netIncome >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
              {formatAmount(netIncome)}
            </p>
          </div>
        </div>
      )}
    </button>
  );
};

export default ProfitAndLossCard;
